'use client';

import { useEffect, useState, type RefObject } from 'react';
import { scrollStateOf, type FilmstripScrollState } from './filmstrip-scroll';

/**
 * Recheck the scrollport and its tiles when layout, rendered content or the
 * scroll position changes.
 *
 * THREE PRIMITIVE STATES rather than one object, because the scroll listener
 * fires on every frame of a smooth scroll: React bails out of a set that
 * repeats a primitive, so the arrows re-render only around the frame an end is
 * reached or left. An object built per event would re-render on every one.
 */
export const useFilmstripOverflow = (ref: RefObject<HTMLElement | null>, content: unknown): FilmstripScrollState => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    const strip = ref.current;
    if (!strip) { return; }

    const measure = () => {
      const state = scrollStateOf(strip);
      setHasOverflow(state.hasOverflow);
      setCanScrollPrev(state.canScrollPrev);
      setCanScrollNext(state.canScrollNext);
    };
    measure();

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(strip);
    Array.from(strip.children).forEach((tile) => observer?.observe(tile));
    strip.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);

    return () => {
      observer?.disconnect();
      strip.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [ref, content]);

  return { hasOverflow, canScrollPrev, canScrollNext };
};
